import ownerSurfaceContract from "./ownerSurfaceContract.json";

export type OwnerNavId = "home" | "missions" | "chat" | "agent-os" | "operations" | "control" | "routines" | "brain" | "systems" | "settings";
export type OwnerSurface = OwnerNavId;

export interface OwnerSurfaceContract {
  route: OwnerSurface;
  productOwner: "SIMO";
  primary: boolean;
  purpose: string;
  targetWriteLane: string;
  replacementSurface: string;
}

export const OWNER_NAVIGATION: ReadonlyArray<{ id: OwnerNavId; label: string }> = [
  { id: "home", label: "Home" },
  { id: "missions", label: "Missionen" },
  { id: "chat", label: "Chat" },
  { id: "agent-os", label: "Agent OS Studio" },
  { id: "operations", label: "Operations" },
  { id: "control", label: "Control Plane" },
  { id: "brain", label: "Brain" },
];

export const OWNER_SURFACE_CONTRACT = {
  ...ownerSurfaceContract.surfaces,
  control: { route: "control", productOwner: "SIMO", primary: true, purpose: "Owner-Control-Plane: Queue, Supervisor, Missions & Runs, Gates, Resources, Scopes, Findings, Evidence", targetWriteLane: "read-only (Leseflaeche)", replacementSurface: "" },
} as Record<OwnerNavId, OwnerSurfaceContract>;

export const OWNER_WORKSPACE_LABELS: Record<OwnerNavId, string> = {
  home: "SIMO Mission Center",
  missions: "Missionen",
  chat: "Chat",
  "agent-os": "Agent OS Studio",
  operations: "Operations Center",
  control: "Control Plane",
  routines: "Agent OS Studio · Routinen",
  brain: "Shared Brain Studio",
  systems: "Systeme",
  settings: "Einstellungen",
};

/**
 * OPNAV1 Slice 5 — Control-Plane-Bereiche der Owner-Shell. Eine klare
 * Owner-Control-Plane-Navigation: alle Bereiche sind über diese Fläche
 * erreichbar (klar sichtbar oder maximal ein eindeutiger Klick entfernt).
 * Deep-Link: `#/control/<bereich>` — Browser Back/Forward funktioniert.
 */
export type ControlPlaneAreaId =
  | "overview"
  | "queue"
  | "missions-runs"
  | "supervisor"
  | "agents"
  | "agent-room"
  | "gates"
  | "resources"
  | "scopes"
  | "findings"
  | "evidence"
  | "attention";

export const CONTROL_PLANE_AREAS: ReadonlyArray<{ id: ControlPlaneAreaId; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "queue", label: "Queue" },
  { id: "missions-runs", label: "Missions & Runs" },
  { id: "supervisor", label: "Supervisor" },
  { id: "agents", label: "Agents" },
  { id: "agent-room", label: "Agent Room" },
  { id: "gates", label: "Human Gates" },
  { id: "resources", label: "Resources" },
  { id: "scopes", label: "Scopes & Authority" },
  { id: "findings", label: "Findings" },
  { id: "evidence", label: "Evidence & Reports" },
  { id: "attention", label: "Attention" },
];

/**
 * OWNEXP Slice 1 — DNA 03 §4: die Bereiche werden nach Owner-Job gruppiert
 * (vier verbindliche Gruppen statt 12 gleichrangiger Pills, DC-23). IDs,
 * Labels und Deep-Links (`#/control/<bereich>`) bleiben unverändert —
 * nur die Gruppierung/Anordnung ist Navigation, keine neue Bereichsstruktur.
 */
export interface ControlPlaneAreaGroup {
  id: "lage" | "arbeit" | "entscheidung-beweis" | "rahmen";
  label: string;
  areas: ControlPlaneAreaId[];
}

export const CONTROL_PLANE_AREA_GROUPS: ReadonlyArray<ControlPlaneAreaGroup> = [
  { id: "lage", label: "Lage", areas: ["overview", "attention"] },
  { id: "arbeit", label: "Arbeit", areas: ["missions-runs", "queue", "supervisor", "agent-room"] },
  { id: "entscheidung-beweis", label: "Entscheidung & Beweis", areas: ["gates", "findings", "evidence"] },
  { id: "rahmen", label: "Rahmen", areas: ["agents", "resources", "scopes"] },
];

export function controlPlaneAreaFromHash(hash: string): ControlPlaneAreaId {
  const raw = hash.replace(/^#\/control\/?/, "").split("?")[0] ?? "";
  const treffer = CONTROL_PLANE_AREAS.find((area) => area.id === raw);
  return treffer?.id ?? "overview";
}

/** Top-Level-Hash → Surface (z. B. `#/operations` → `operations`).
 *  `#/control/<bereich>` landet auf der Control-Plane-Fläche; der Bereich
 *  selbst wird dort aus dem Hash gelesen (controlPlaneAreaFromHash). */
export function ownerSurfaceFromHash(hash: string): OwnerNavId | null {
  const raw = hash.replace(/^#\/?/, "").split("?")[0] ?? "";
  if (!raw) return null;
  if (raw === "control" || raw.startsWith("control/")) return "control";
  const treffer = OWNER_NAVIGATION.find((eintrag) => eintrag.id === raw);
  return treffer?.id ?? null;
}

export type OwnerNavigationTarget = { kind: "surface"; surface: OwnerSurface };

export function ownerNavigationTarget(id: OwnerNavId): OwnerNavigationTarget {
  return { kind: "surface", surface: OWNER_SURFACE_CONTRACT[id].route };
}

export type SettingsNavigationTab =
  | "policy"
  // SIMO-OWNER-SESSION-MANAGEMENT-UI-1: die Geraeteliste ist eine
  // Sicherheitsflaeche und gehoert deshalb unter "settings", nicht unter
  // "systems" — sie beantwortet "wer ist als ich angemeldet", keine
  // Systemkonfiguration.
  | "sessions"
  | "connectors"
  | "permissions"
  | "secrets"
  | "memory"
  | "routines"
  | "models";

export function ownerSurfaceForSettingsTab(tab: SettingsNavigationTab): OwnerSurface {
  if (tab === "memory") return "brain";
  if (tab === "routines") return "agent-os";
  if (tab === "connectors" || tab === "permissions" || tab === "secrets" || tab === "models") return "systems";
  return "settings";
}

export function ownerLogoutNavigationState(): { surface: OwnerSurface; activeNav: OwnerNavId } {
  return { surface: "home", activeNav: "home" };
}
